import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type MultiSelectChipsOption = {
  id: string;
  label: string;
};

type Props = {
  id?: string;
  label: string;
  required?: boolean;
  value: string[];
  options: MultiSelectChipsOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  onChange: (value: string[]) => void;
  error?: string;
  disabled?: boolean;
  emptyMessage?: string;
};

export default function MultiSelectChips({
  id,
  label,
  required,
  value,
  options,
  placeholder = 'Search and select…',
  searchPlaceholder = 'Search…',
  onChange,
  error,
  disabled = false,
  emptyMessage,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return options.filter((o) => (!qq ? true : o.label.toLowerCase().includes(qq)));
  }, [options, q]);

  const selectedLabels = value.map((uid) => options.find((o) => o.id === uid)?.label ?? uid);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQ('');
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setQ('');
    }
  }, [disabled]);

  return (
    <div className="al-f">
      <div className="al-l">
        {label} {required ? <span className="req">*</span> : null}
      </div>
      <div ref={wrapRef} className={`chipSel${open ? ' is-open' : ''}`}>
        <button
          type="button"
          id={id}
          className="chipSelBtn"
          disabled={disabled}
          aria-expanded={open}
          aria-controls={listId}
          aria-invalid={error ? true : undefined}
          style={error ? { borderColor: 'var(--red)' } : undefined}
          onClick={() => {
            if (disabled) return;
            if (open) setQ('');
            setOpen(!open);
          }}
        >
          <div className="chipRow">
            {value.length === 0 ? <span className="chipPh">{placeholder}</span> : null}
            {selectedLabels.map((lbl, i) => (
              <span key={value[i]} className="chip">
                {lbl}
                {!disabled ? (
                  <button
                    type="button"
                    className="chipX"
                    aria-label={`Remove ${lbl}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(value.filter((x) => x !== value[i]));
                    }}
                  >
                    ×
                  </button>
                ) : null}
              </span>
            ))}
          </div>
          <span className="chipCaret" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
        {open && !disabled ? (
          <div id={listId} className="chipSelMenu" role="listbox">
            <input
              ref={searchRef}
              className="chipSelSearch"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="chipSelList">
              {filtered.length === 0 ? (
                <div className="chipSelEmpty">{emptyMessage ?? t('common.noOptionsFound')}</div>
              ) : (
                filtered.map((o) => {
                  const checked = value.includes(o.id);
                  return (
                    <label key={o.id} className="chipSelOpt">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          e.target.checked ? onChange([...value, o.id]) : onChange(value.filter((x) => x !== o.id))
                        }
                      />
                      <span>{o.label}</span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
      {error ? (
        <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
